import type { Dispatch } from "redux";
import {
  DEFAULT_POSA_ORDER,
  POSAOrder,
  POSAOrderItem,
  addPOSAToBasketEnd,
  addPOSAToBasketStart,
  setCurrentPOSAOrder,
  setPOSAOrderIsRecomputing
} from "./reducer";
import { RootStateI } from "../index";
import Axios, { AxiosResponse } from "axios";
import CONFIG from "../../model/TSConfig";
import { HEADER_TOKEN } from "../../utils/constants";
import { setError } from "../actions/app";
import { tt } from "../../utils/i18n/i18n";

export const removePOSAOrderItem =
  (index: number) => async (dispatch: Dispatch, getState: () => RootStateI) => {
    const state = getState();
    const order = cloneOrder(state);
    order.items = [...order.items];
    order.items.splice(index, 1);
    await updatePOSAOrderThunk(order)(dispatch, getState);
  };

export const changePOSAOrderItemQuantityThunk =
  (index: number, qty: number) =>
  async (dispatch: Dispatch, getState: () => RootStateI) => {
    if (isNaN(qty)) {
      return;
    }

    const state = getState();
    const order = cloneOrder(state);
    order.items = [...order.items];
    order.items[index].qty = qty;

    await updatePOSAOrderThunk(order)(dispatch, getState);
  };

export const addPOSAToBasket =
  (posaName: string, posaTypeId: string) =>
  async (dispatch: Dispatch, getState: () => RootStateI) => {
    const state = getState();
    const accountToDisplay = state.user.accountToDisplay;
    const allPOSAType = state.wheel.posaTypes;
    const allPOSAPrices = state.wheel.posaPrices;
    const currentPOSAOrder = state.saas.currentPOSAOrder;
    const isAlreadySelected =
      currentPOSAOrder.items?.some((posa) => posa.posaTypeId === posaTypeId) ||
      false;

    let newItems: POSAOrderItem[];

    if (isAlreadySelected) {
      newItems = currentPOSAOrder.items.filter(
        (item) => item.posaTypeId !== posaTypeId
      );
    } else {
      const posaType = allPOSAType.find(
        (pt) => pt._id.toString() === posaTypeId
      );

      if (posaType == null) {
        dispatch(setError(`Error: no POSA type with ID ${posaTypeId}`));
        return;
      }

      const pricesList = allPOSAPrices.find(
        (price) => price._id === posaType.helloPrintSlug
      );

      const isUsePriceList =
        posaType.hasPriceList &&
        pricesList != null &&
        Object.keys(pricesList).length;

      const qty = 0; // computeMinQtyForPosa(posaType.hasPriceList);

      const orderItem: POSAOrderItem = {
        name: posaName,
        unitPrice: !isUsePriceList ? posaType.unitPrice : undefined,
        qty,
        posaTypeId,
        businessUserId: accountToDisplay?.isAll
          ? undefined
          : accountToDisplay?.trackingID
      };

      newItems = [...(currentPOSAOrder.items || []), orderItem];
    }

    const posaOrder: POSAOrder = {
      ...currentPOSAOrder,
      items: newItems
    };

    dispatch(addPOSAToBasketStart(posaTypeId));
    try {
      await updatePOSAOrderThunk(posaOrder)(dispatch, getState);
    } finally {
      dispatch(addPOSAToBasketEnd(posaTypeId));
    }
  };

export const updatePOSAOrderThunk =
  (newOrder: POSAOrder) =>
  async (dispatch: Dispatch, getState: () => RootStateI) => {
    if (newOrder === DEFAULT_POSA_ORDER) {
      dispatch(setCurrentPOSAOrder(DEFAULT_POSA_ORDER));
      return;
    }

    const state = getState();
    const token = state.user.masterToken;

    if (!token) {
      alert(tt("noTokenAlert"));
      return;
    }

    const backendOrder = await recompute(newOrder, token, dispatch);

    if (backendOrder) {
      dispatch(setCurrentPOSAOrder(backendOrder));
    }
  };

async function recompute(order: POSAOrder, token: string, dispatch: Dispatch) {
  if (!token) {
    return undefined;
  }

  try {
    dispatch(setPOSAOrderIsRecomputing(true));

    const res: AxiosResponse<POSAOrder> = await Axios({
      method: "POST",
      url: `${CONFIG.backendURL}/posa/recomputePrices`,
      headers: {
        [HEADER_TOKEN]: token
      },
      data: order
    });
    return res.data;
  } catch (err) {
  } finally {
    dispatch(setPOSAOrderIsRecomputing(false));
  }
}

function cloneOrder(state: RootStateI) {
  return JSON.parse(JSON.stringify(state.saas.currentPOSAOrder));
}
